(() => {
  'use strict';

  if (!location.pathname.includes('/collections/') || location.pathname.includes('/products/')) return;

  const SECTION = '.shopify-section--main-collection';
  const GRID = '#collection product-list.product-list';
  const STOCK = 'filter.v.availability';
  const HISTORY_KEY = 'auCollectionScroll';
  let state = null;
  let section = null;
  let sectionObserver = null;
  let initialController = null;
  let pendingFacetGrid = null;
  let initializing = true;
  let interaction = 0;
  let bindFrame = 0;
  let scrollFrame = 0;

  const isSlow = () => navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '');
  const alive = value => value === state && !value.disposed && value.grid.isConnected;
  const usable = value => alive(value) && !value.suspended;
  const pageNumber = value => Math.max(1, Number.parseInt(value, 10) || 1);
  const sectionId = value => value?.id?.replace(/^shopify-section-/, '') || '';

  function publicUrl(value) {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Collection URL changed');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  }

  function contextKey(url, pageParam) {
    const clean = new URL(url);
    clean.searchParams.delete(pageParam);
    clean.searchParams.delete('section_id');
    clean.searchParams.delete('sections');
    clean.searchParams.sort();
    return clean.pathname + clean.search;
  }

  function pageUrl(value, page) {
    const url = new URL(value.baseUrl);
    if (page === 1) url.searchParams.delete(value.pageParam);
    else url.searchParams.set(value.pageParam, String(page));
    return url;
  }

  function injectStockInputs(root) {
    root.querySelectorAll('facets-form form, form.facets').forEach(form => {
      let input = form.querySelector('input[name="filter.v.availability"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = STOCK;
        form.appendChild(input);
      }
      input.value = '1';
    });
  }

  function notifyAdded(root) {
    document.dispatchEvent(new CustomEvent('au:collection:products-added', { detail: { root } }));
  }

  async function fetchSection(url, ownerSection, controller) {
    const target = new URL(url);
    target.hash = '';
    target.searchParams.set('section_id', sectionId(ownerSection));
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(target.href, {
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Collection request failed');
      const html = await response.text();
      if (controller.signal.aborted) throw new Error('Collection request cancelled');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const freshSection = parsed.getElementById(ownerSection.id) || parsed.querySelector(SECTION);
      const freshGrid = parsed.querySelector(GRID);
      if (!freshSection || !freshGrid) throw new Error('Collection content unavailable');
      return { section: freshSection, grid: freshGrid, pager: freshSection.querySelector('#collection .pagination') };
    } finally {
      clearTimeout(timeout);
    }
  }

  function sameMarket(before, after) {
    return before.dataset.auCountry === after.dataset.auCountry && before.dataset.auCurrency === after.dataset.auCurrency;
  }

  async function applyInitialStock() {
    const originalUrl = location.href;
    const url = publicUrl(originalUrl);
    if (url.searchParams.get(STOCK) === '1') return;
    const originalGrid = document.querySelector(GRID);
    const originalCollection = document.getElementById('collection');
    const originalSection = document.querySelector(SECTION);
    if (!originalGrid || !originalCollection || !originalSection) return;
    const revision = interaction;
    const controller = new AbortController();
    initialController = controller;
    url.searchParams.set(STOCK, '1');
    originalCollection.setAttribute('aria-busy', 'true');
    try {
      const fresh = await fetchSection(url, originalSection, controller);
      if (controller.signal.aborted || revision !== interaction || location.href !== originalUrl || !originalSection.isConnected || document.querySelector(GRID) !== originalGrid || !sameMarket(originalGrid, fresh.grid)) return;
      originalSection.replaceChildren(...document.importNode(fresh.section, true).childNodes);
      history.replaceState(history.state, '', url.href);
      injectStockInputs(originalSection);
      notifyAdded(document.getElementById('collection'));
    } catch (_) {
    } finally {
      if (initialController === controller) initialController = null;
      originalCollection.removeAttribute('aria-busy');
      document.getElementById('collection')?.removeAttribute('aria-busy');
    }
  }

  function metadata(grid, page) {
    const canonical = new URL(grid.dataset.auCanonical || location.href, location.origin);
    const param = grid.dataset.auPageParam || 'page';
    canonical.searchParams.delete('section_id');
    canonical.searchParams.delete('sections');
    canonical.hash = '';
    if (page === 1) canonical.searchParams.delete(param);
    else canonical.searchParams.set(param, String(page));
    return { canonical: canonical.href, title: grid.dataset.auTitle || document.title };
  }

  function registerBatch(value, nodes, grid) {
    const page = pageNumber(grid.dataset.auPage);
    const cards = nodes.filter(node => node.matches?.('product-card'));
    cards.forEach(card => {
      if (!card.dataset.auOriginHandle) card.dataset.auOriginHandle = card.getAttribute('handle') || '';
      card.dataset.auScrollPage = String(page);
    });
    const batch = { page, nodes, cards, ...metadata(grid, page) };
    value.batches.push(batch);
    value.loadedPages.add(page);
    return batch;
  }

  function nextUrl(value, pager = value.pager) {
    const href = pager?.querySelector('a[rel="next"]')?.getAttribute('href');
    if (!href) return null;
    const url = publicUrl(href);
    const page = pageNumber(url.searchParams.get(value.pageParam));
    if (value.loadedPages.has(page) || page <= value.batches[value.batches.length - 1].page) return null;
    return pageUrl(value, page);
  }

  function updateCount(value) {
    if (!value.count) return;
    const loaded = value.grid.querySelectorAll(':scope > product-card').length;
    const label = value.controls.dataset.countLabel || '__loaded__ / __total__';
    value.count.textContent = label.replaceAll('__loaded__', String(loaded)).replaceAll('__total__', String(value.total));
  }

  function setStatus(value, kind) {
    if (!value.count) return;
    if (kind === 'loading') value.count.textContent = value.controls.dataset.loadingLabel || '';
    else if (kind === 'error') value.count.textContent = value.controls.dataset.errorLabel || '';
    else updateCount(value);
  }

  function setBusy(value, busy) {
    value.button.disabled = busy;
    value.button.setAttribute('aria-busy', String(busy));
    if (busy) value.grid.setAttribute('aria-busy', 'true');
    else value.grid.removeAttribute('aria-busy');
  }

  function stopObservers(value) {
    value.prefetchObserver?.disconnect();
    value.appendObserver?.disconnect();
    value.prefetchObserver = null;
    value.appendObserver = null;
  }

  function abortPending(value) {
    value.request?.controller.abort();
    value.request = null;
    value.prefetched = null;
    value.sequence++;
    value.appending = false;
    setBusy(value, false);
  }

  function warmImages(value, grid) {
    if (isSlow()) return;
    const style = getComputedStyle(value.grid);
    const tracks = style.gridTemplateColumns.split(' ').filter(track => /^\d/.test(track));
    const columns = Math.max(1, tracks.length || Number.parseInt(style.getPropertyValue('--product-list-column-count'), 10) || 2);
    const gap = Number.parseFloat(style.columnGap) || 0;
    const width = (value.grid.clientWidth - gap * (columns - 1)) / columns;
    const compact = matchMedia('(max-width: 699px)').matches
      ? value.grid.getAttribute('mobile-items-per-row') === '3'
      : value.grid.getAttribute('desktop-layout') === 'thumbnails';
    const sequence = value.sequence;
    const queue = [];
    let filled = 0;
    for (const card of grid.querySelectorAll(':scope > product-card')) {
      const source = card.querySelector('.product-card__image--primary, img.product-card__image');
      const span = !compact && card.classList.contains('product-card--highlight') ? Math.min(2, columns) : 1;
      if (source?.src) queue.push({ source, size: Math.ceil(width * span + gap * (span - 1)) });
      filled += span;
      if (filled >= columns || queue.length >= 4) break;
    }
    const run = async () => {
      while (queue.length && usable(value) && sequence === value.sequence) {
        const { source, size } = queue.shift();
        await new Promise(resolve => {
          const image = new Image();
          image.onload = image.onerror = resolve;
          image.fetchPriority = 'low';
          image.sizes = size > 0 ? `${size}px` : (source.sizes || '100vw').replace(/^auto,\s*/, '');
          if (source.srcset) image.srcset = source.srcset;
          image.src = source.src;
        });
      }
    };
    run();
    run();
  }

  function dispose(value) {
    if (!value) return;
    value.disposed = true;
    stopObservers(value);
    abortPending(value);
    clearTimeout(value.facetTimer);
    value.events.abort();
  }

  async function prepareNext(value) {
    if (!usable(value)) throw new Error('Collection changed');
    const url = nextUrl(value);
    if (!url) return null;
    const key = url.href;
    if (value.prefetched?.key === key) return value.prefetched;
    if (value.request?.key === key) return value.request.promise;
    value.request?.controller.abort();
    const controller = new AbortController();
    const sequence = value.sequence;
    const request = { key, controller, promise: null };
    request.promise = fetchSection(url, value.section, controller).then(async fresh => {
      if (!usable(value) || sequence !== value.sequence || controller.signal.aborted) throw new Error('Collection changed');
      const expectedPage = pageNumber(url.searchParams.get(value.pageParam));
      if (!sameMarket(value.grid, fresh.grid) || pageNumber(fresh.grid.dataset.auPage) !== expectedPage || fresh.grid.dataset.auPageParam !== value.grid.dataset.auPageParam) throw new Error('Collection context changed');
      warmImages(value, fresh.grid);
      await new Promise(resolve => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 100 });
        else setTimeout(resolve, 0);
      });
      if (!usable(value) || sequence !== value.sequence || controller.signal.aborted) throw new Error('Collection changed');
      const nodes = [...fresh.grid.children].map(node => document.importNode(node, true));
      if (!nodes.some(node => node.matches?.('product-card'))) throw new Error('Next collection page is empty');
      const fragment = document.createDocumentFragment();
      nodes.forEach(node => {
        node.removeAttribute('reveal-on-scroll');
        node.querySelectorAll('[reveal-on-scroll]').forEach(child => child.removeAttribute('reveal-on-scroll'));
        fragment.appendChild(node);
      });
      const result = { key, url, ...fresh, nodes, fragment };
      value.prefetched = result;
      return result;
    }).finally(() => {
      if (value.request === request) value.request = null;
    });
    value.request = request;
    return request.promise;
  }

  async function loadNext(value, manual = false) {
    if (!alive(value)) return;
    if (value.suspended) {
      if (manual) location.assign(location.href);
      return;
    }
    if (value.appending || (!manual && value.autoFailed)) return;
    if (!manual && (value.controlsHovered || value.controlsFocused)) return;
    if (!nextUrl(value)) return;
    const sequence = value.sequence;
    const focusNewProducts = manual && value.button.matches(':focus-visible');
    value.appending = true;
    if (manual) value.autoFailed = false;
    setBusy(value, true);
    setStatus(value, 'loading');
    try {
      const fresh = await prepareNext(value);
      if (!fresh || !usable(value) || sequence !== value.sequence) return;
      if (!manual && (value.controlsHovered || value.controlsFocused)) {
        updateCount(value);
        return;
      }
      const page = pageNumber(fresh.grid.dataset.auPage);
      if (value.loadedPages.has(page)) return;
      const { nodes, fragment } = fresh;
      value.grid.appendChild(fragment);
      registerBatch(value, nodes, fresh.grid);
      value.prefetched = null;
      value.total = Number(fresh.grid.dataset.auTotal) || value.total;
      if (fresh.pager && value.pager) value.pager.replaceChildren(...document.importNode(fresh.pager, true).childNodes);
      else if (value.pager) value.pager.replaceChildren();
      value.autoFailed = false;
      notifyAdded(value.grid);
      updateCount(value);
      value.button.hidden = !nextUrl(value);
      if (value.button.hidden) stopObservers(value);
      if (focusNewProducts) nodes.find(node => node.matches?.('product-card'))?.querySelector('a[href*="/products/"]')?.focus({ preventScroll: true });
      scheduleScroll();
    } catch (_) {
      if (usable(value) && sequence === value.sequence) {
        value.autoFailed = true;
        value.prefetched = null;
        setStatus(value, 'error');
      }
    } finally {
      if (sequence === value.sequence) {
        value.appending = false;
        if (alive(value)) setBusy(value, false);
      }
    }
  }

  function startObservers(value) {
    stopObservers(value);
    if (!usable(value) || !('IntersectionObserver' in window) || !nextUrl(value)) return;
    if (!isSlow()) value.prefetchObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || !usable(value) || value.autoFailed) return;
        const sequence = value.sequence;
        prepareNext(value).catch(() => {
          if (usable(value) && sequence === value.sequence && !value.appending) {
            value.autoFailed = true;
            setStatus(value, 'error');
          }
        });
      }, { rootMargin: '2000px 0px' });
    value.appendObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNext(value);
    }, { rootMargin: '700px 0px' });
    value.prefetchObserver?.observe(value.controls);
    value.appendObserver.observe(value.controls);
  }

  function resumeNearEnd(value) {
    if (!usable(value) || value.controlsHovered || value.controlsFocused) return;
    const rect = value.controls.getBoundingClientRect();
    if (rect.top <= innerHeight + 700 && rect.bottom >= -700) loadNext(value);
  }

  function batchAtViewport(value) {
    const line = innerHeight * 0.5;
    let active = value.batches[0];
    for (const batch of value.batches) {
      const first = batch.nodes.find(node => node.isConnected);
      if (first && first.getBoundingClientRect().top <= line) active = batch;
    }
    return active;
  }

  function updateHead(value, batch) {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = batch.canonical;
    if (batch.title) document.title = batch.title;
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', batch.canonical);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', batch.title);
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => {
      const url = new URL(link.href, location.origin);
      if (batch.page === 1) url.searchParams.delete(value.pageParam);
      else url.searchParams.set(value.pageParam, String(batch.page));
      url.searchParams.delete('section_id');
      link.href = url.href;
    });
  }

  function writePage(value, batch, anchor) {
    if (!usable(value) || !batch) return;
    const url = pageUrl(value, batch.page);
    const existing = history.state && typeof history.state === 'object' ? history.state : {};
    const record = {
      version: 1,
      context: contextKey(url, value.pageParam),
      country: value.grid.dataset.auCountry || '',
      currency: value.grid.dataset.auCurrency || '',
      page: batch.page,
      savedAt: Date.now(),
      ...(anchor || {})
    };
    history.replaceState({ ...existing, [HISTORY_KEY]: record }, '', url.href);
    if (value.activePage !== batch.page) updateHead(value, batch);
    value.activePage = batch.page;
  }

  function scheduleScroll() {
    if (scrollFrame) return;
    scrollFrame = setTimeout(() => {
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        const value = state;
        if (!value || !usable(value) || value.restoring) return;
        const batch = batchAtViewport(value);
        if (batch && value.activePage !== batch.page) writePage(value, batch);
      });
    }, 100);
  }

  function saveAnchor(preferredCard) {
    const value = state;
    if (!value || !usable(value)) return;
    const cards = [...value.grid.querySelectorAll(':scope > product-card')];
    const visible = cards.filter(card => {
      const rect = card.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight;
    });
    const card = preferredCard && cards.includes(preferredCard) ? preferredCard : visible.reduce((best, current) => !best || Math.abs(current.getBoundingClientRect().top) < Math.abs(best.getBoundingClientRect().top) ? current : best, null);
    if (!card) return;
    const batch = value.batches.find(item => item.page === pageNumber(card.dataset.auScrollPage));
    writePage(value, batch, { handle: card.dataset.auOriginHandle || card.getAttribute('handle') || '', offset: card.getBoundingClientRect().top });
  }

  function restoreAnchor(value, record) {
    const navigation = performance.getEntriesByType?.('navigation')[0]?.type;
    if (navigation !== 'back_forward' && navigation !== 'reload') return;
    if (!record?.handle || !Number.isFinite(record.offset) || Date.now() - record.savedAt > 1800000 || record.context !== contextKey(value.baseUrl, value.pageParam) || record.country !== (value.grid.dataset.auCountry || '') || record.currency !== (value.grid.dataset.auCurrency || '')) return;
    const card = [...value.grid.querySelectorAll(':scope > product-card')].find(node => node.dataset.auOriginHandle === record.handle || node.getAttribute('handle') === record.handle);
    if (!card) return;
    const revision = interaction;
    value.restoring = true;
    const restore = () => {
      if (!usable(value) || revision !== interaction || !card.isConnected) return;
      const difference = card.getBoundingClientRect().top - record.offset;
      if (Math.abs(difference) > 1) window.scrollTo({ top: Math.max(0, scrollY + difference), behavior: 'instant' });
    };
    requestAnimationFrame(() => requestAnimationFrame(restore));
    [100, 300, 650].forEach(delay => setTimeout(restore, delay));
    setTimeout(() => {
      value.restoring = false;
      if (usable(value)) scheduleScroll();
    }, 850);
  }

  function bind() {
    if (initializing) return;
    const grid = document.querySelector(GRID);
    if (grid === state?.grid) return;
    dispose(state);
    state = null;
    const collection = document.getElementById('collection');
    const ownerSection = document.querySelector(SECTION);
    const controls = collection?.querySelector('[data-au-collection-controls]');
    const button = controls?.querySelector('[data-au-load-more]');
    if (!grid || !collection || !ownerSection || !controls || !button) return;
    const saved = history.state?.[HISTORY_KEY];
    const waitingForFacet = grid === pendingFacetGrid;
    if (!waitingForFacet) pendingFacetGrid = null;
    const value = {
      grid, collection, section: ownerSection, controls, button,
      count: controls.querySelector('[data-au-count]'),
      pager: collection.querySelector('.pagination'),
      baseUrl: publicUrl(location.href),
      pageParam: grid.dataset.auPageParam || 'page',
      total: Number(grid.dataset.auTotal) || Number(collection.dataset.auProductCount) || 0,
      batches: [], loadedPages: new Set(), events: new AbortController(),
      sequence: 0, activePage: null, disposed: false, suspended: waitingForFacet,
      appending: false, autoFailed: false, request: null, prefetched: null,
      restoring: false, navigationCard: null, controlsHovered: false, controlsFocused: false
    };
    state = value;
    registerBatch(value, [...grid.children], grid);
    controls.hidden = false;
    button.hidden = !nextUrl(value);
    setBusy(value, false);
    injectStockInputs(ownerSection);
    updateCount(value);
    button.addEventListener('click', () => loadNext(value, true), { signal: value.events.signal });
    controls.addEventListener('pointerenter', () => { value.controlsHovered = true; }, { signal: value.events.signal });
    controls.addEventListener('pointerleave', () => {
      value.controlsHovered = false;
      resumeNearEnd(value);
    }, { signal: value.events.signal });
    controls.addEventListener('focusin', () => { value.controlsFocused = true; }, { signal: value.events.signal });
    controls.addEventListener('focusout', event => {
      if (event.relatedTarget && controls.contains(event.relatedTarget)) return;
      value.controlsFocused = false;
      resumeNearEnd(value);
    }, { signal: value.events.signal });
    const savedAnchorMatches = saved?.handle && Number.isFinite(saved.offset) && Date.now() - saved.savedAt <= 1800000 && saved.context === contextKey(value.baseUrl, value.pageParam) && saved.country === (grid.dataset.auCountry || '') && saved.currency === (grid.dataset.auCurrency || '') && saved.page === pageNumber(grid.dataset.auPage);
    writePage(value, value.batches[0], savedAnchorMatches ? { handle: saved.handle, offset: saved.offset } : undefined);
    if (!waitingForFacet) restoreAnchor(value, saved);
    else {
      setStatus(value, 'loading');
      value.facetTimer = setTimeout(() => {
        if (alive(value) && value.suspended) setStatus(value, 'error');
      }, 12500);
    }
    startObservers(value);
  }

  function scheduleBind() {
    if (bindFrame) return;
    bindFrame = requestAnimationFrame(() => {
      bindFrame = 0;
      bind();
    });
  }

  function watchSection() {
    const next = document.querySelector(SECTION);
    if (next === section && sectionObserver) return;
    sectionObserver?.disconnect();
    section = next;
    if (!section) return;
    sectionObserver = new MutationObserver(() => {
      if (document.querySelector(GRID) !== state?.grid) scheduleBind();
    });
    sectionObserver.observe(section, { childList: true });
  }

  document.addEventListener('facet:update', event => {
    const target = event.detail?.url;
    if (!(target instanceof URL) || target.origin !== location.origin || target.pathname !== location.pathname || !target.pathname.includes('/collections/')) return;
    target.searchParams.set(STOCK, '1');
    new URL(location.href).searchParams.forEach((entry, key) => {
      if ((key.startsWith('utm_') || /^(fbclid|gclid|gbraid|wbraid|msclkid)$/.test(key)) && !target.searchParams.has(key)) target.searchParams.append(key, entry);
    });
    interaction++;
    initialController?.abort();
    pendingFacetGrid = document.querySelector(GRID);
    const value = state;
    if (!value) return;
    value.suspended = true;
    stopObservers(value);
    abortPending(value);
    setStatus(value, 'loading');
    clearTimeout(value.facetTimer);
    value.facetTimer = setTimeout(() => {
      if (alive(value) && value.suspended) setStatus(value, 'error');
    }, 12500);
  }, true);

  ['pointerdown', 'keydown', 'wheel', 'touchstart', 'change'].forEach(type => document.addEventListener(type, () => {
    interaction++;
    if (state) {
      state.restoring = false;
      state.navigationCard = null;
    }
    if (initializing) initialController?.abort();
  }, { capture: true, passive: true }));

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    const card = link?.closest('product-card');
    if (!card || !state?.grid.contains(card) || link.matches('[data-au-native-swatch]') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
    state.navigationCard = card;
    saveAnchor(card);
  }, true);

  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll, { passive: true });
  window.addEventListener('pagehide', () => {
    saveAnchor(state?.navigationCard);
    if (state) {
      stopObservers(state);
      abortPending(state);
    }
    initialController?.abort();
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    watchSection();
    if (state && usable(state)) {
      state.navigationCard = null;
      state.appending = false;
      state.autoFailed = false;
      updateCount(state);
      startObservers(state);
      scheduleScroll();
    } else scheduleBind();
  });
  window.addEventListener('popstate', () => {
    const value = state;
    if (!value || !usable(value)) return;
    const url = publicUrl(location.href);
    if (contextKey(url, value.pageParam) !== contextKey(value.baseUrl, value.pageParam)) {
      location.reload();
      return;
    }
    const batch = value.batches.find(item => item.page === pageNumber(url.searchParams.get(value.pageParam)));
    if (!batch) {
      location.reload();
      return;
    }
    if (batch.page === value.activePage) return;
    restoreAnchor(value, history.state?.[HISTORY_KEY]);
    value.activePage = batch.page;
    updateHead(value, batch);
  });

  async function start() {
    injectStockInputs(document);
    watchSection();
    try {
      await applyInitialStock();
    } finally {
      initializing = false;
      watchSection();
      bind();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

